import React, { useRef, useState } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import { uniqueId, isEqual } from 'lodash';
import Reorderable from '@dashboard/components/Reorderable';
import Button from '@dashboard/components/Button';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Label } from '@dashboard/components/Form';
import { PlusIcon } from '@heroicons/react/24/outline';
import { confirm } from '@dashboard/lib/confirm';
import {
  isPriceDraftDirty,
  isBlankOrZeroFixedPrice,
  shouldBlockZeroPrice,
  reconcileCurrencyOnSave,
  reconcileCurrencyToSingle,
  fillMissingPriceReferences,
  normalizeRevealFlags,
  persistedFields,
} from './priceHelpers';
import { cn } from '@dashboard/lib/utils';
import { toast } from '@dashboard/context/ToastContext';
import { PriceEditProvider, usePriceEdit } from './PriceEditContext';
import PriceCard from './PriceCard';
import PriceForm from './PriceForm';
import PricesHeaderCard, { describeGrouping } from './PricesHeaderCard';
import PricesSettingsForm from './PricesSettingsForm';
import useProduct from '../../useProduct';
import Divider from '@/dashboard/components/Divider';

export const DEFAULT_NEW_PRICE = {
  billingType: 'one_time',
  __pricing: 'fixed',
  __schedule: 'subscription',
  __trial: 'none',
  __discountOpen: false,
  __startDateOpen: false,
  __setupFeeOpen: false,
  label: '',
  description: '',
  badge: '',
  reference: '',
  amount: '',
  currency: 'usd',
  enabled: true,
  hidden: false,
  hasBeenPurchased: false,
  payWhatYouWant: false,
  pwywSuggestedPrice: '',
  discountedFromPrice: '',
  setupFee: '',
  setupFeeMultipliesWithQuantity: false,
  interval: 'month',
  intervalCount: 1,
  trialPeriodDays: '',
  planIterations: '',
  billingCycleAnchorConfig: { enabled: false, dayOfMonth: 1, month: '' },
};

function priceId(p) {
  return p._id || p.key;
}

/**
 * "Price must be more than 0" under a COLLAPSED price card, so a fixed 0/blank
 * price in a 2+-price product stays visible after you close its form (where the
 * open form's AmountBlockedError would otherwise hide the error). Reads live
 * Formik + edit state via context so it re-renders on value/expand changes
 * regardless of the reorderable list's memoization; renders nothing while the
 * card is open (the form shows the error there instead).
 */
function CollapsedCardBlockError({ index, id, isNew }) {
  const { values, initialValues } = useFormikContext();
  const { editingPriceId } = usePriceEdit();
  if (String(editingPriceId) === String(id)) return null;
  const blocked = shouldBlockZeroPrice(values?.prices?.[index], {
    prices: values?.prices,
    draftId: values?.__draftPriceId,
    isNew,
    initialValues,
  });
  if (!blocked) return null;
  return <p className="px-1 pb-2 text-sm text-destructive">Price must be more than 0</p>;
}

/**
 * Inner component that can access PriceEditContext after the provider mounts.
 */
function PricesFieldArray({
  isNew = false,
  onPersistReorder,
  variants,
  groupingAxis,
  titleField,
  onCancelPricingSwitch,
}) {
  const {
    values,
    initialValues,
    setFieldValue,
    setValues,
    submitForm,
    setFieldError,
    setFieldTouched,
    errors,
  } = useFormikContext();

  // Show an inline "amount" error on a blocked price. FormError (Formik's
  // ErrorMessage) only renders a touched field, so mark it touched too — without
  // validation (shouldValidate=false), which would otherwise wipe this manual
  // error (the schema doesn't flag a 0 amount).
  const blockPriceAmount = idx => {
    setFieldTouched(`prices.${idx}.amount`, true, false);
    setFieldError(`prices.${idx}.amount`, 'Price must be more than 0');
  };
  // Which face of the prices card is showing. Only meaningful in the edit
  // flow with 2+ committed prices, where the card renders.
  const [activeTab, setActiveTab] = useState('prices');
  const { product } = useProduct();
  const { editingPriceId, setEditingPriceId, requestCloseEditing, editingSnapshotRef } =
    usePriceEdit();
  const prices = values.prices || [];

  // The price most recently ADDED in this editing session (a committed
  // draft), kept as an identity snapshot that survives the temp-key ->
  // server-_id swap on refetch (the reference is filled at commit time and
  // persisted). The next draft seeds its billing/pricing from this price —
  // NOT from whatever happens to sit last in the list, which changes under
  // drag-reorder. When nothing was added this session (fresh page open) the
  // seeding falls back to the last listed price.
  const lastAddedPriceRef = useRef(null);
  const recordLastAddedPrice = price => {
    if (!price) return;
    lastAddedPriceRef.current = {
      key: price.key ?? null,
      id: price._id != null ? String(price._id) : null,
      reference: price.reference || null,
    };
  };
  const findLastAddedPrice = list => {
    const rec = lastAddedPriceRef.current;
    if (!rec) return null;
    return (
      list.find(
        p =>
          (rec.key != null && p.key === rec.key) ||
          (rec.id != null && p._id != null && String(p._id) === rec.id) ||
          (rec.reference != null && p.reference === rec.reference),
      ) ?? null
    );
  };

  const defaultPriceId = values.defaultPriceId;
  const draftId = values.__draftPriceId ?? null;
  // True whenever a committed price card is open for editing (either flow). Used to
  // hide "Add price" while a card is open — matching the event-tickets UX, which hides
  // "Add ticket" while a ticket is being edited. Gate on the price still existing so
  // deleting the price you're editing releases it (rather than leaving the list with
  // no add button and no open card).
  const isEditingCommittedCard =
    editingPriceId != null &&
    editingPriceId !== draftId &&
    prices.some(p => priceId(p) === editingPriceId);
  // Create flow only: while a committed price is being edited, the always-visible
  // new-price form is greyed out + made inert.
  const isEditingCommitted = isNew && isEditingCommittedCard;

  return (
    <FieldArray name="prices">
      {() => {
        async function handleAddPrice() {
          // Mutual exclusion: an open committed price and a new-price draft can't
          // coexist — collapse the open price first, honoring its unsaved-changes
          // prompt. If the user keeps it open, abort the add. Build on the
          // post-close prices it returns: a stale `prices` closure here would
          // re-introduce edits the user just chose to discard.
          let basePrices = prices;
          if (editingPriceId != null && editingPriceId !== draftId) {
            // The single-price-edit (flat form) has no open accordion card — its
            // edits autosave — so a non-null editingPriceId there is a stale
            // leftover from a card that was collapsed when the list dropped back
            // to the flat form (e.g. expanding the lone card cancelled the draft).
            // Just clear it: routing through requestCloseEditing would run the
            // unmounted card's stale close handler (re-introducing the discarded
            // draft), and leaving it set reopens the committed card alongside the
            // new draft. Only the multi-card view has a real open card to prompt+close.
            const inFlatSingleMode = !isNew && draftId == null && prices.length === 1;
            if (inFlatSingleMode) {
              editingSnapshotRef.current = null;
              setEditingPriceId(null);
            } else {
              const { ok, revertedPrices } = await requestCloseEditing();
              if (!ok) return;
              if (revertedPrices) basePrices = revertedPrices;
            }
          }
          // Reconcile the currency before committing the finished price(s): a deferred
          // per-price change (the just-edited draft on a different currency) applies now
          // — confirm on a multi-price product, then every price + the product adopt it.
          // Cancelling the confirm aborts the add. The new draft inherits the result.
          const reconciled = await reconcileCurrencyToSingle({
            values: { ...values, prices: basePrices },
            confirm,
          });
          if (!reconciled) return;
          basePrices = reconciled.prices;
          if (reconciled.currency !== values.currency) {
            await setFieldValue('currency', reconciled.currency);
          }
          const reconciledCurrency = reconciled.currency || 'usd';
          const draftIdx = draftId != null ? basePrices.findIndex(p => priceId(p) === draftId) : -1;
          const draft = draftIdx >= 0 ? basePrices[draftIdx] : null;
          // A fixed price can't be 0/blank once it joins a multi-price arrangement.
          // The price being locked in is the draft (create / edit "Add another
          // price") or, with no draft (single-price edit "Add price"), the existing
          // sole price. Block it with an inline error under its amount rather than
          // silently defaulting the amount to 0.
          const committingIdx = draftIdx >= 0 ? draftIdx : basePrices.length === 1 ? 0 : -1;
          if (committingIdx >= 0 && isBlankOrZeroFixedPrice(basePrices[committingIdx])) {
            blockPriceAmount(committingIdx);
            return;
          }

          const newKey = uniqueId('price-');
          // Carry the billing type, schedule, and pricing choice (fixed/PWYW) from
          // the price just added — or, with no open draft, the last price ADDED
          // this session (a reorder must not change what seeds the next draft),
          // falling back to the last listed price — so building a list of similar
          // prices (e.g. several subscription tiers, or PWYW variants) doesn't
          // reset to the default each time. Price-specific VALUES (amount/label/
          // suggested price/…) still reset.
          // Carrying payWhatYouWant is safe since the explicit-add change: a draft
          // never rides "Create checkout" when committed prices exist, so the old
          // phantom-second-PWYW-price hazard (a pristine carried flag counting as
          // "configured" in prepareSubmitValues) can no longer occur.
          const source =
            draft || findLastAddedPrice(basePrices) || basePrices[basePrices.length - 1] || {};
          const carriedBillingType = source.billingType ?? DEFAULT_NEW_PRICE.billingType;
          const carriedSchedule = source.__schedule ?? DEFAULT_NEW_PRICE.__schedule;
          const carriedPwyw = source.payWhatYouWant === true;
          const next = [...basePrices];
          next.push({
            ...DEFAULT_NEW_PRICE,
            key: newKey,
            currency: reconciledCurrency,
            billingType: carriedBillingType,
            __schedule: carriedSchedule,
            payWhatYouWant: carriedPwyw,
            __pricing: carriedPwyw ? 'pwyw' : 'fixed',
            // A carried payment-plan needs the (required) number of payments. The
            // schedule radio's default-to-4 only runs on a click — which doesn't happen
            // for a carried schedule — so seed it here (keeping the source's count if
            // it had one), otherwise the new plan starts with an empty, invalid count.
            planIterations:
              carriedBillingType === 'recurring' && carriedSchedule === 'payment-plan'
                ? source.planIterations > 0
                  ? source.planIterations
                  : 4
                : DEFAULT_NEW_PRICE.planIterations,
          });
          // Committing the finished price(s): reset any reveal ticked without a
          // value, and give every priced row a reference so it shows immediately
          // instead of only after "Create checkout". The new empty draft is a
          // no-op for both (no values yet).
          const committed = fillMissingPriceReferences(
            next.map(normalizeRevealFlags),
            reconciledCurrency,
          );
          // The just-committed draft becomes the "last added" price the NEXT
          // draft will seed from (recorded post-fill so the reference is set).
          if (draft) recordLastAddedPrice(committed.find(c => priceId(c) === priceId(draft)));
          setFieldValue('prices', committed);
          setFieldValue('__draftPriceId', newKey);
        }

        // Set the pre-selected price; on a saved product persist immediately.
        async function handleSetDefault(pid) {
          await setFieldValue('defaultPriceId', pid);
          if (!isNew) await submitForm();
        }

        // Clear the pre-selected price so the checkout opens with nothing chosen,
        // forcing the customer to make a pricing decision. Persist immediately on
        // a saved product.
        async function handleRemoveDefault() {
          await setFieldValue('defaultPriceId', null);
          if (!isNew) await submitForm();
        }

        // Enable/disable a price. The kebab greys the Disable action only for the
        // last enabled price (see disableReason), so the last-enabled toast here is
        // a backstop. Disabling the preselected price is allowed — it clears the
        // preselection (a disabled price can't be the default; the API rejects it).
        // On a saved product persist immediately.
        async function handleToggleEnabled(index) {
          const price = prices[index];
          const enabledCount = prices.filter(
            p => priceId(p) !== draftId && p.enabled !== false,
          ).length;
          const isDisabling = price.enabled !== false;
          if (isDisabling && enabledCount <= 1) {
            toast({ title: 'Keep at least one price enabled', type: 'error' });
            return;
          }
          const next = prices.map((p, i) => (i === index ? { ...p, enabled: !p.enabled } : p));
          // Disabling the preselected price clears the preselection (a disabled
          // price can't be the default; the API rejects it). Update prices AND
          // defaultPriceId in one setValues so the immediate submit sees the
          // cleared default — two awaited setFieldValue calls would let submitForm
          // read a stale defaultPriceId (only the first update has flushed).
          const clearsDefault =
            isDisabling && defaultPriceId && String(priceId(price)) === String(defaultPriceId);
          await setValues({
            ...values,
            prices: next,
            ...(clearsDefault && { defaultPriceId: null }),
          });
          if (!isNew) await submitForm();
        }

        // Duplicate the price in place, collapsed (not opened); on a saved product
        // persist immediately so it doesn't need a separate Save.
        async function handleDuplicatePrice(index) {
          const live = prices[index];
          // Duplicating the price whose form is open copies its committed
          // snapshot — copying live values would launder unsaved edits into a
          // saved row. `enabled`/`order` ride from the live row: both are
          // action-owned (autosaved), so the live values are the committed ones.
          const isOpenRow =
            editingPriceId != null && String(priceId(live)) === String(editingPriceId);
          const src =
            isOpenRow && editingSnapshotRef.current
              ? { ...editingSnapshotRef.current, enabled: live.enabled, order: live.order }
              : live;
          const newKey = uniqueId('price-');
          const {
            _id,
            key,
            reference,
            hasBeenPurchased,
            chargeCount,
            subscribers,
            createdAt,
            updatedAt,
            _reorderKey,
            _originalIndex,
            ...rest
          } = src;
          // Drop `reference` so the API derives a fresh, unique one for the copy
          // (it dedupes with _2/_3); reusing the source's reference would collide.
          const copy = { ...rest, key: newKey, hasBeenPurchased: false };
          await setFieldValue('prices', [
            ...prices.slice(0, index + 1),
            copy,
            ...prices.slice(index + 1),
          ]);
          if (!isNew) await submitForm();
        }

        // Delete the price; on a saved product persist immediately (delete + save)
        // instead of requiring a separate Save click.
        async function handleDeletePrice(index) {
          const price = prices[index];
          // A price that saved variants are scoped to can't be deleted — the
          // variants would be left broken: hidden on the checkout (the dead id
          // never matches a selectable price) and unfixable in the editor.
          // Explain and block; the API rejects it as the backstop.
          const referencing = (variants || []).filter(v =>
            (v.priceIds || []).map(String).includes(String(price._id)),
          );
          if (referencing.length > 0) {
            // One line per variant option ("Group – Option"); a variant is
            // scoped as a whole, so all of its options are listed.
            const references = referencing.flatMap(v => {
              const groupName = v.name || 'Unnamed variant';
              const options = v.options || [];
              if (options.length === 0) return [groupName];
              return options.map(o => `${groupName} – ${o.name || 'Unnamed option'}`);
            });
            const shown = references.slice(0, 6);
            const more = references.length - shown.length;
            await confirm("Can't delete this price", {
              // Radix renders the description as a <p>, so the list is built
              // from block-level spans rather than a <ul>.
              description: (
                <>
                  To delete this price, first remove it from these variant options:
                  <span className="mt-2 block">
                    {shown.map((label, i) => (
                      <span
                        key={i}
                        className="block font-semibold text-gray-900 dark:text-foreground"
                      >
                        {label}
                      </span>
                    ))}
                    {more > 0 && <span className="block">and {more} more</span>}
                  </span>
                </>
              ),
              confirm: 'Delete',
              confirmDisabled: true,
            });
            return;
          }
          const confirmed = await confirm('Delete this price?', { confirm: 'Delete' });
          if (!confirmed) return;
          // Deleting the price whose form is open closes the form (nothing left
          // to edit) — and clears the snapshot so the submit doesn't try to
          // substitute a row that no longer exists.
          if (editingPriceId != null && String(priceId(price)) === String(editingPriceId)) {
            editingSnapshotRef.current = null;
            setEditingPriceId(null);
          }
          // Deleting the preselected price clears the preselection so its id
          // doesn't dangle. Update prices AND defaultPriceId in one setValues so
          // the immediate submit sees the cleared default (see handleToggleEnabled).
          const clearsDefault = defaultPriceId && String(priceId(price)) === String(defaultPriceId);
          await setValues({
            ...values,
            prices: prices.filter((_, i) => i !== index),
            ...(clearsDefault && { defaultPriceId: null }),
          });
          if (!isNew) await submitForm();
        }

        // Reorder prices; on a saved product persist immediately.
        async function handleReorder(reordered) {
          // Re-stamp `order` from the new sequence. Prices persist as a Mongoose
          // subdoc array that's in-place-merged by _id on save (array position
          // never moves), and every read path orders by the `order` field —
          // the storefront `$sort: { order: 1 }` and the default-price
          // resolution. Without re-stamping, a reorder never changes `order`, so
          // the new sequence (and the implicit "first price" preselect derived
          // from it) reverts on save/refresh.
          const reorderedWithOrder = reordered.map((p, i) => ({ ...p, order: i }));
          await setFieldValue('prices', reorderedWithOrder);

          // Persist the restamped array explicitly rather than via submitForm().
          // react-beautiful-dnd's onDragEnd fires outside React's discrete-event
          // flush, so submitForm() here would read Formik's pre-reorder committed
          // values (its values ref only refreshes after the setFieldValue re-render
          // commits) and save the old `order`. onPersistReorder sends this array.
          if (!isNew) await onPersistReorder?.(reorderedWithOrder);
        }

        // Committed prices only — the unsaved draft must not make the lone
        // committed price look deletable/disableable (defence in depth under
        // the isPriceFormOpen gate).
        const enabledCount = prices.filter(
          p => priceId(p) !== draftId && p.enabled !== false,
        ).length;

        // Split prices into committed (all except the current draft) and the draft.
        // Preserve original indices so fieldPrefix uses prices[i].
        const committedEntries = [];
        let draftEntry = null;

        prices.forEach((price, i) => {
          const pid = priceId(price);
          if (draftId != null && pid === draftId) {
            draftEntry = { price, originalIndex: i };
          } else {
            committedEntries.push({ price, originalIndex: i });
          }
        });

        // Currency is shared by every price + the product, and stays editable (a change
        // prompts before propagating to all prices — see PriceForm) until a price has
        // been purchased. Stripe prices are immutable, so once there's a sale the
        // currency is locked for every price (the selector stays visible but disabled).
        const currencyLocked = prices.some(p => p.hasBeenPurchased);

        // Reorderable items from committed prices only.
        const reorderableItems = committedEntries.map(({ price, originalIndex }) => ({
          ...price,
          _reorderKey: priceId(price) || String(originalIndex),
          _originalIndex: originalIndex,
        }));

        // A product with exactly one committed price (and no draft being added)
        // shows a flat inline price form on the edit flow — not the full
        // multi-price element (count header + reorderable cards + per-price
        // kebab). Disable/delete are blocked for a lone price and preselect is
        // meaningless, so "Add another price" replaces the kebab. Edits persist
        // via an explicit Save — never an autosave, whose reseed would recompute
        // the tick-to-reveal flags and close an empty "Discounted from"/"Setup
        // fee" reveal under the user mid-edit.
        const isSinglePriceEdit = !isNew && committedEntries.length === 1 && !draftEntry;
        if (isSinglePriceEdit) {
          const only = committedEntries[0];
          // Dirty against the last-saved values (initialValues is reseeded by
          // every save), judged on persisted fields only — reveal flags and other
          // UI-only state don't count, matching the price card's dirty check.
          const savedPrice = (initialValues.prices || [])[only.originalIndex];
          // Dirty if the price OR the product title changed — the single-price
          // form's Save persists both, so the "Changes not saved" hint must
          // react to a title edit too (the title field doesn't autosave here).
          const titleDirty = (values.title || '') !== (initialValues.title || '');
          // TODO(pricing-type-revival): a mode flip (variants pricing → base price)
          // must also light the "Changes not saved" hint — it persists via this Save.
          const modeDirty =
            (values.pricingType || 'single') !== (initialValues.pricingType || 'single');
          const isDirty =
            titleDirty ||
            modeDirty ||
            !isEqual(persistedFields(only.price), persistedFields(savedPrice));

          // Mirrors PriceCard.handleSave: reset value-less reveals, apply a
          // deferred currency change (no confirm needed for a lone price), persist.
          async function handleSaveSinglePrice() {
            // The flat form has no open card, so a non-null editingPriceId /
            // snapshot here is a stale leftover from a card that was collapsed
            // when the list dropped to the flat form (see handleAddPrice).
            // Clear both so the submit can't substitute the stale snapshot for
            // the row being saved.
            if (editingPriceId != null) {
              editingSnapshotRef.current = null;
              setEditingPriceId(null);
            }
            await setFieldValue('prices', prices.map(normalizeRevealFlags));
            const ok = await reconcileCurrencyOnSave({
              values,
              setFieldValue,
              confirm,
              index: only.originalIndex,
            });
            if (!ok) return;
            await submitForm();
          }

          // TODO(pricing-type-revival): onCancelPricingSwitch swaps "Add price"
          // for a Cancel while a variants→base switch is pending.
          return (
            <div className="rounded-lg border border-stone-200 dark:border-border p-3">
              {/* Single-price edit: the product title shares the box with the
                  price, saved together by the Save button below. */}
              {titleField}
              <Divider />
              <PriceForm
                fieldPrefix={`prices[${only.originalIndex}]`}
                currencyLocked={currencyLocked}
                price={only.price}
                lockBilling={true}
                isNew={false}
                singlePrice={true}
              />
              <div className="flex items-center justify-between gap-2">
                {!onCancelPricingSwitch && (
                  <Button
                    type="button"
                    appearance="secondary"
                    onClick={handleAddPrice}
                    className="gap-1.5"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add price
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <FormSubmit.State visible={isDirty} />
                  {onCancelPricingSwitch && (
                    <Button type="button" appearance="light" onClick={onCancelPricingSwitch}>
                      Cancel
                    </Button>
                  )}
                  <Button type="button" appearance="primary" onClick={handleSaveSinglePrice}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        // The card (heading, count, Prices / Settings tabs) replaces the
        // "Prices added" label once there is more than one price to pick
        // from - the settings behind it only matter then. Never in the
        // create flow, whose single submit has nothing to save them to yet.
        const showHeaderCard = !isNew && committedEntries.length >= 2;
        const showSettingsTab = showHeaderCard && activeTab === 'settings';
        const showPricesTab = !showSettingsTab;

        return (
          <>
            <Divider className="mb-4"></Divider>
            {showHeaderCard && (
              <PricesHeaderCard
                name={product?.pricePicker?.title?.trim() || 'Prices'}
                priceCount={committedEntries.length}
                detail={describeGrouping(values, groupingAxis)}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}
            <div className={cn('space-y-4', showHeaderCard && 'mt-2')}>
              {!showHeaderCard && committedEntries.length > 0 && (
                <Label>Prices added · {committedEntries.length}</Label>
              )}

              {showSettingsTab && (
                <PricesSettingsForm
                  groupingAxis={groupingAxis}
                  onClose={() => setActiveTab('prices')}
                />
              )}

              {showPricesTab && committedEntries.length > 0 && (
                <div className="border-t border-border">
                  <Reorderable
                    id="prices-list"
                    // Portal the drag clone to <body> ONLY in the create flow, whose
                    // centered/carousel layout establishes a fixed-positioning
                    // containing block that throws the clone off-screen. On the edit
                    // page the clone already tracks the cursor, and portaling the
                    // dragged node out of the droppable subtree breaks rbd's drop
                    // detection (reorder silently no-ops), so leave it in place there.
                    usePortal={isNew}
                    items={reorderableItems}
                    onReorder={reordered => {
                      // Rebuild the full prices array: reordered committed + draft (if any) appended.
                      // eslint-disable-next-line no-unused-vars
                      const reorderedWithDraft = reordered.map(
                        ({ _reorderKey, _originalIndex, ...rest }) => rest,
                      );
                      if (draftEntry) {
                        reorderedWithDraft.push(draftEntry.price);
                      }
                      handleReorder(reorderedWithDraft);
                    }}
                    renderItem={({ item: price, dragHandleProps }) => {
                      const pid = priceId(price);
                      const originalIndex = price._originalIndex;
                      // Per-price API error (e.g. "Price amount cannot be negative"),
                      // mapped from the save response in ProductDetailsForm. Shown
                      // inline beneath the price so it's clear which one is invalid.
                      const apiError = errors?.prices?.[originalIndex]?._apiError;
                      // A price is pre-selected only when the merchant explicitly set
                      // it as the default — no implicit "first price" pre-selection.
                      const isDefault = !!(
                        defaultPriceId &&
                        pid &&
                        String(pid) === String(defaultPriceId)
                      );

                      // Why a price can't be deleted (shown as a tooltip on the
                      // disabled Delete item in PriceCard) — empty = deletable.
                      // A purchased price and the last enabled price can't be
                      // deleted. The preselected price CAN be deleted — the delete
                      // handler clears the preselection so its id doesn't dangle.
                      // Create flow: everything is deletable — nothing is purchased
                      // yet and the create submit blocks zero configured prices
                      // (hasConfiguredPrice in ProductDetailsForm).
                      const deleteReason = isNew
                        ? ''
                        : price.hasBeenPurchased
                          ? "Purchased prices can't be deleted."
                          : price.enabled !== false && enabledCount <= 1
                            ? 'Add another price before deleting this one.'
                            : '';
                      const canDelete = !deleteReason;

                      // Preselect (star) availability. The star + Set/Remove
                      // preselected stay visible whenever 2+ prices exist — disabling a
                      // sibling must never make the preselected price's star or menu item
                      // disappear. Only "Set as preselected" (a non-default price) can be
                      // unavailable — a hidden or disabled price can't be preselected, and
                      // there's no choice to make with only one active price. "Remove
                      // preselected" (the default price) is always allowed, so it never
                      // gets a reason.
                      const showPreselect = committedEntries.length > 1;
                      let preselectReason = '';
                      if (showPreselect && !isDefault) {
                        if (price.hidden === true) {
                          preselectReason = 'Show this price to preselect it.';
                        } else if (price.enabled === false) {
                          preselectReason = 'Enable this price to preselect it.';
                        } else if (enabledCount <= 1) {
                          preselectReason =
                            'Preselection applies when 2 or more prices are active.';
                        }
                      }

                      // Why a price can't be disabled (tooltip on the greyed Disable
                      // item) — empty = disableable. Only the last enabled price is
                      // blocked (a page needs one active price). The preselected price
                      // CAN be disabled — the toggle handler clears the preselection
                      // (a disabled price can't be the default). Enable is always
                      // allowed, so an already-disabled price has no reason.
                      const disableReason =
                        price.enabled === false
                          ? ''
                          : enabledCount <= 1
                            ? 'Keep at least one price enabled.'
                            : '';

                      return (
                        <React.Fragment key={price._reorderKey}>
                          <PriceCard
                            price={price}
                            fieldPrefix={`prices[${originalIndex}]`}
                            index={originalIndex}
                            isFirstPrice={originalIndex === 0}
                            currencyLocked={currencyLocked}
                            isDefault={isDefault}
                            onSetDefault={() => handleSetDefault(pid)}
                            onRemoveDefault={handleRemoveDefault}
                            onToggleEnabled={() => handleToggleEnabled(originalIndex)}
                            dragHandleProps={dragHandleProps}
                            canDelete={canDelete}
                            deleteReason={deleteReason}
                            disableReason={disableReason}
                            showPreselect={showPreselect}
                            preselectReason={preselectReason}
                            onDelete={() => handleDeletePrice(originalIndex)}
                            onDuplicate={() => handleDuplicatePrice(originalIndex)}
                            isNew={isNew}
                          />
                          {apiError && (
                            <p className="px-1 pb-2 text-sm text-destructive">{apiError}</p>
                          )}
                          <CollapsedCardBlockError index={originalIndex} id={pid} isNew={isNew} />
                        </React.Fragment>
                      );
                    }}
                  />
                </div>
              )}
              {showPricesTab &&
                draftEntry &&
                (committedEntries.length > 0 && !isNew ? (
                  // Edit flow, adding an additional price: bordered draft with
                  // Cancel / Save, and the global "Add another price" button is hidden.
                  <div className="rounded-lg border border-stone-200 dark:border-border p-3">
                    <PriceForm
                      // Key by the draft's id so adding another price re-mounts a
                      // fresh form. Without this, the consolidated amount/currency
                      // block (a static-keyed custom field) is reused in place and
                      // keeps the previous price's values.
                      key={priceId(draftEntry.price)}
                      fieldPrefix={`prices[${draftEntry.originalIndex}]`}
                      currencyLocked={currencyLocked}
                      price={draftEntry.price}
                      lockBilling={false}
                      isNew={isNew}
                    />
                    <div className="flex items-center justify-end gap-2">
                      {/* Same "Changes not saved" affordance as the rest of the app,
                          shown once the draft holds real input. */}
                      <FormSubmit.State visible={isPriceDraftDirty(draftEntry.price)} />
                      <Button
                        type="button"
                        appearance="light"
                        onClick={async () => {
                          await setFieldValue(
                            'prices',
                            prices.filter((_, i) => i !== draftEntry.originalIndex),
                          );
                          await setFieldValue('__draftPriceId', null);
                        }}
                      >
                        Cancel
                      </Button>
                      {/* Save is never disabled (the reporter's anti-pattern). A
                          fixed price joining this multi-price product can't be 0/blank
                          — block it with an inline error under the amount rather than
                          defaulting it to 0. PWYW (min 0) still saves. */}
                      <Button
                        type="button"
                        appearance="primary"
                        onClick={async () => {
                          const idx = draftEntry.originalIndex;
                          const d = prices[idx];
                          if (isBlankOrZeroFixedPrice(d)) {
                            blockPriceAmount(idx);
                            return;
                          }
                          let nextPrices = prices;
                          // Committing the draft: reset any reveal ticked without a
                          // value, then give it (and any other priced row still missing
                          // one) a reference so it's populated before "Create checkout".
                          nextPrices = fillMissingPriceReferences(
                            nextPrices.map(normalizeRevealFlags),
                            values.currency,
                          );
                          if (nextPrices !== prices) await setFieldValue('prices', nextPrices);
                          // Committed via Save: this is now the "last added"
                          // price the next draft seeds from.
                          recordLastAddedPrice(nextPrices[idx]);
                          // A deferred currency change on the draft applies on Save —
                          // confirm (when there's >1 price) + propagate to every price
                          // before persisting; abort the save if cancelled.
                          const ok = await reconcileCurrencyOnSave({
                            values,
                            setFieldValue,
                            confirm,
                            index: idx,
                          });
                          if (!ok) return;
                          await setFieldValue('__draftPriceId', null);
                          if (!isNew) await submitForm();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Create flow (and the initial draft): the new-price form is
                  // always visible inline. It greys out + becomes inert while a
                  // committed price is being edited.
                  <div
                    className={cn(isEditingCommitted && 'pointer-events-none opacity-60')}
                    inert={isEditingCommitted || undefined}
                  >
                    <PriceForm
                      // Key by the draft's id so adding another price re-mounts a
                      // fresh form. Without this, the consolidated amount/currency
                      // block (a static-keyed custom field) is reused in place and
                      // keeps the previous price's values.
                      key={priceId(draftEntry.price)}
                      fieldPrefix={`prices[${draftEntry.originalIndex}]`}
                      currencyLocked={currencyLocked}
                      price={draftEntry.price}
                      lockBilling={false}
                      isNew={isNew}
                    />
                  </div>
                ))}

              {showPricesTab &&
                !isEditingCommittedCard &&
                !(draftEntry && committedEntries.length > 0 && !isNew) && (
                  <Button
                    type="button"
                    appearance="secondary"
                    // Full width in the create flow (sits above the full-width
                    // "Create checkout" button); compact in the edit flow. Both
                    // keep the default 32px (h-8) height, matching the Save and
                    // "Create checkout" buttons for a consistent control height.
                    onClick={handleAddPrice}
                    className={cn('gap-1.5', isNew && 'w-full justify-center')}
                  >
                    <PlusIcon className="w-4 h-4" />
                    {/* Create flow always says "Add another price" — the visible
                        form counts as the first price. Edit flow: "Add price". */}
                    {isNew ? 'Add another price' : 'Add price'}
                  </Button>
                )}
            </div>
          </>
        );
      }}
    </FieldArray>
  );
}

/**
 * PricesList manages the prices[] array on the product form.
 *
 * Props:
 *   currency – the product-level currency (used for new prices)
 *   startCollapsed – true on the edit page; all committed cards start collapsed
 *   variants – the product's saved variants; deleting a price they're scoped
 *     to is blocked (the variants would be left broken)
 *   editingSnapshotRef – optional ref shared with ProductDetailsForm; holds the
 *     committed snapshot of the price whose form is open so product submits
 *     can substitute it for the in-flight row (see PriceEditContext)
 *   titleField – optional product-title element rendered at the top of the
 *     single-price edit box (saved by that form's Save button); ignored in the
 *     multi-price / create layouts, where the title sits at the top instead
 *   onCancelPricingSwitch – set while an unsaved variants→base-price switch is
 *     pending; the single-price form then shows Cancel (this callback) instead
 *     of "Add price"
 */
export default function PricesList({
  startCollapsed = false,
  isNew = false,
  onEditingChange,
  onPersistReorder,
  variants,
  editingSnapshotRef,
  groupingAxis,
  titleField,
  onCancelPricingSwitch,
}) {
  const { values } = useFormikContext();
  const prices = values.prices || [];
  const defaultPriceId = values.defaultPriceId;

  // On create, no committed card is auto-expanded — the draft form is the focus.
  // On edit, keep startCollapsed behavior (defaultPriceId card open if set).
  const initialEditingId = React.useMemo(() => {
    if (startCollapsed) return null;
    // Fresh create: __draftPriceId is set, no committed rows → no card to open.
    if (values.__draftPriceId != null) return null;
    if (defaultPriceId) return String(defaultPriceId);
    const first = prices[0];
    if (!first) return null;
    return String(priceId(first) || '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // computed once on mount — intentionally no deps

  return (
    <PriceEditProvider
      initialEditingId={initialEditingId}
      onEditingChange={onEditingChange}
      snapshotRef={editingSnapshotRef}
    >
      <PricesFieldArray
        startCollapsed={startCollapsed}
        isNew={isNew}
        onPersistReorder={onPersistReorder}
        variants={variants}
        groupingAxis={groupingAxis}
        titleField={titleField}
        onCancelPricingSwitch={onCancelPricingSwitch}
      />
    </PriceEditProvider>
  );
}
